import type { ResponsePostMerchflows_merchflowId_ExportsBatch } from "@CommonApi/merchflows/_merchflowId_/exports/batch";
import { useState } from "react";
import { useApi } from "src/api";
import { useToasts } from "src/components/Toasts";
import { Button, Color, Flex, Text } from "src/elements";
import { openNativeDownload } from "src/utils/downloadFile";
import { pdfExportMessageKeys } from "src/api/merchflows/pdfExportErrors";
import { useTranslation } from "src/i18n";

interface Props {
  merchflowId: number;
  batch?: ResponsePostMerchflows_merchflowId_ExportsBatch | null;
}

export const PdfExportBatchStatus = ({ merchflowId, batch }: Props) => {
  const { getExportBatchPogsApi } = useApi();
  const { toast } = useToasts();
  const { t } = useTranslation("common");
  const [isDownloading, setIsDownloading] = useState(false);

  if (!batch || batch.type !== "pdfV2") return null;

  const downloadZip = async () => {
    setIsDownloading(true);
    try {
      const freshBatch = await getExportBatchPogsApi(merchflowId, batch.batch_id);
      if (freshBatch?.status === "DONE" && freshBatch.url) {
        openNativeDownload(freshBatch.url);
      } else if (freshBatch?.status === "ERROR") {
        toast({ title: t(pdfExportMessageKeys.failed), type: "ERROR" });
      } else if (freshBatch?.status === "DONE") {
        toast({ title: t(pdfExportMessageKeys.download), type: "ERROR" });
      } else if (freshBatch) {
        toast({
          title: t(pdfExportMessageKeys.notReady),
          type: "WARNING",
        });
      }
    } catch (error) {
      console.warn("PDF ZIP download could not start", {
        batchId: batch.batch_id,
        kind: error instanceof Error ? "error" : typeof error,
      });
      toast({ title: t(pdfExportMessageKeys.download), type: "ERROR" });
    } finally {
      setIsDownloading(false);
    }
  };

  if (batch.status === "DONE") {
    return (
      <Flex gap="8px" align="center">
        <Text variant="small1" color={Color.primary}>
          ✓ Export completed
        </Text>
        <Button size="small" isLoading={isDownloading} onClick={downloadZip}>
          Download ZIP
        </Button>
      </Flex>
    );
  }

  if (batch.status === "ERROR") {
    return (
      <Text variant="small1" color={Color.red}>
        {t(pdfExportMessageKeys.failed)}
      </Text>
    );
  }

  const progressLabel = batch.progress
    ? `PDF status (${batch.progress.generated} / ${batch.progress.total})`
    : "Preparing PDF export";
  const isPreparingZip =
    batch.progress !== undefined && batch.progress.generated === batch.progress.total;

  if (isPreparingZip) {
    return <Text variant="small1">Preparing ZIP...</Text>;
  }

  return <Text variant="small1">{progressLabel}</Text>;
};
